import GavelIcon from '@mui/icons-material/Gavel';
import LaunchIcon from '@mui/icons-material/Launch';
import {
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Stack,
  Typography,
} from '@mui/material';

import type { CatalogApp } from '../api/queries';

interface Props {
  app: CatalogApp | null;
  myApps: string[];
  onClose: () => void;
  onRequestAccess: (app: CatalogApp) => void;
}

export function AppDetailDialog({ app, myApps, onClose, onRequestAccess }: Props) {
  if (!app) return null;
  const haveIt = myApps.includes(app.app_name);
  const launchUrl = app.login_url || app.homepage_url;

  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Stack direction="row" spacing={1.5} sx={{ alignItems: 'center', flexWrap: 'wrap' }}>
          <span>{app.display_name ?? app.app_name}</span>
          {app.is_universal && <Chip size="small" color="primary" label="Everyone has this" />}
          {haveIt && !app.is_universal && (
            <Chip size="small" color="success" label="You have access" />
          )}
          {app.requires_approval && (
            <Chip size="small" color="warning" icon={<GavelIcon />} label="Approval required" />
          )}
        </Stack>
      </DialogTitle>
      <DialogContent>
        <Stack spacing={1.5}>
          {app.description && (
            <Typography variant="body2" color="text.secondary">
              {app.description}
            </Typography>
          )}
          <Stack direction="row" spacing={3} sx={{ flexWrap: 'wrap' }}>
            <Detail label="Category" value={app.category ?? '—'} />
            <Detail label="Vendor" value={app.vendor ?? '—'} />
          </Stack>
          {app.entra_registrations && app.entra_registrations.length > 0 && (
            <>
              <Divider />
              <div>
                <Typography variant="caption" color="text.secondary">
                  Entra registrations
                </Typography>
                <Stack direction="row" spacing={1} className="mt-1" sx={{ flexWrap: 'wrap' }}>
                  {app.entra_registrations.map((r) => (
                    <Chip key={r} size="small" variant="outlined" label={r} />
                  ))}
                </Stack>
              </div>
            </>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        {launchUrl && (
          <Button
            startIcon={<LaunchIcon />}
            href={launchUrl}
            target="_blank"
            rel="noopener noreferrer"
          >
            Open
          </Button>
        )}
        {!haveIt && !app.is_universal && (
          <Button variant="contained" onClick={() => onRequestAccess(app)}>
            Request access
          </Button>
        )}
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}

function Detail({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
        {label}
      </Typography>
      <Typography variant="body2">{value}</Typography>
    </div>
  );
}
